import React, { useState } from 'react';

// Custom Oscar-style statue icon
const OscarStatue = ({ size = 24, className = '', fill = 'none', ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    {/* Head */}
    <circle cx="12" cy="3.5" r="2" />
    {/* Body / torso */}
    <path d="M12 5.5 L12 14" />
    {/* Arms holding sword/reel */}
    <path d="M8 8 L12 10 L16 8" />
    {/* Legs / base stand */}
    <path d="M12 14 L9.5 18 L8 18" />
    <path d="M12 14 L14.5 18 L16 18" />
    {/* Pedestal base */}
    <rect x="7" y="19" width="10" height="2.5" rx="1" />
  </svg>
);

/**
 * Movie Award Rating Component
 * Replaces traditional stars with movie award trophies
 * Supports 0.5 increments (0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5)
 */
const AwardRating = ({ 
  value = 0, 
  onChange, 
  readonly = false, 
  size = 'md',
  showValue = true,
  showLabel = false 
}) => {
  const [hoverValue, setHoverValue] = useState(null);
  
  const sizes = {
    sm: { icon: 16, gap: 1, text: 'text-xs' },
    md: { icon: 24, gap: 2, text: 'text-sm' },
    lg: { icon: 32, gap: 3, text: 'text-base' },
    xl: { icon: 40, gap: 4, text: 'text-lg' }
  };
  
  const currentSize = sizes[size] || sizes.md;
  const displayValue = hoverValue !== null ? hoverValue : value;

  const getRatingLabel = (rating) => {
    if (rating === 0) return 'Not Rated';
    if (rating <= 1) return 'Poor';
    if (rating <= 2) return 'Fair';
    if (rating <= 3) return 'Good';
    if (rating <= 4) return 'Great';
    return 'Masterpiece';
  };

  const handleClick = (newValue) => {
    if (readonly || !onChange) return;
    // Toggle off if clicking same value
    if (newValue === value) {
      onChange(0);
    } else {
      onChange(newValue);
    }
  };

  const handleMouseMove = (e, index) => {
    if (readonly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isHalf = x < rect.width / 2;
    setHoverValue(isHalf ? index + 0.5 : index + 1);
  };

  const renderAward = (index) => {
    const fillValue = displayValue - index;
    const isFilled = fillValue >= 1;
    const isHalf = fillValue > 0 && fillValue < 1;

    return (
      <div
        key={index}
        className={`relative ${readonly ? '' : 'cursor-pointer'}`}
        onClick={() => handleClick(hoverValue !== null ? hoverValue : index + 1)}
        onMouseMove={(e) => handleMouseMove(e, index)}
        onMouseLeave={() => setHoverValue(null)}
        style={{ width: currentSize.icon, height: currentSize.icon }}
      >
        {/* Background (empty) award */}
        <OscarStatue
          className="absolute inset-0 text-zinc-600 transition-colors"
          size={currentSize.icon}
        />
        
        {/* Filled award with clip for half */}
        {(isFilled || isHalf) && (
          <div 
            className="absolute inset-0 overflow-hidden transition-all"
            style={{ width: isHalf ? '50%' : '100%' }}
          >
            <OscarStatue
              className="text-yellow-500 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]"
              size={currentSize.icon}
              fill="currentColor"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className={`flex items-center gap-${currentSize.gap}`}>
        <div className="flex">
          {[0, 1, 2, 3, 4].map(renderAward)}
        </div>
        
        {showValue && displayValue > 0 && (
          <span className={`${currentSize.text} text-yellow-500 font-bold ml-2`}>
            {displayValue.toFixed(1)}
          </span>
        )}
      </div>
      
      {showLabel && (
        <span className={`${currentSize.text} text-gray-400`}>
          {getRatingLabel(displayValue)}
        </span>
      )}
    </div>
  );
};

/**
 * Compact inline rating display (read-only)
 */
export const AwardRatingInline = ({ value = 0, size = 'sm' }) => {
  const sizes = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20
  };
  
  const iconSize = sizes[size] || sizes.sm;
  
  if (value === 0) return null;
  
  return (
    <div className="flex items-center gap-1">
      <OscarStatue 
        className="text-yellow-500" 
        size={iconSize} 
        fill="currentColor"
      />
      <span className="text-yellow-500 font-medium text-sm">
        {value.toFixed(1)}
      </span>
    </div>
  );
};

/**
 * Rating with content type selector (for TV shows)
 */
export const ContentRating = ({
  contentType = 'movie', // 'movie', 'tv'
  value = 0,
  seasonRatings = {}, // { season_number: rating }
  episodeRatings = {}, // { "season_episode": rating }
  onRateContent,
  onRateSeason,
  onRateEpisode,
  seasons = [], // Array of season objects with episodes
  readonly = false
}) => {
  const [activeTab, setActiveTab] = useState('overall');
  const [selectedSeason, setSelectedSeason] = useState(null);

  if (contentType === 'movie') {
    return (
      <div className="space-y-2">
        <label className="text-gray-400 text-sm block">Your Rating</label>
        <AwardRating 
          value={value} 
          onChange={onRateContent} 
          readonly={readonly}
          showLabel
        />
      </div>
    );
  }

  // TV Show rating with tabs
  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-700 pb-2">
        <button
          onClick={() => { setActiveTab('overall'); setSelectedSeason(null); }}
          className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
            activeTab === 'overall' 
              ? 'bg-yellow-600/20 text-yellow-500 border-b-2 border-yellow-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Overall
        </button>
        <button
          onClick={() => setActiveTab('seasons')}
          className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
            activeTab === 'seasons' 
              ? 'bg-yellow-600/20 text-yellow-500 border-b-2 border-yellow-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          By Season
        </button>
        <button
          onClick={() => setActiveTab('episodes')}
          className={`px-3 py-1.5 rounded-t text-sm font-medium transition-colors ${
            activeTab === 'episodes' 
              ? 'bg-yellow-600/20 text-yellow-500 border-b-2 border-yellow-500' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          By Episode
        </button>
      </div>

      {/* Overall Rating */}
      {activeTab === 'overall' && (
        <div className="space-y-2">
          <label className="text-gray-400 text-sm block">Rate the entire series</label>
          <AwardRating 
            value={value} 
            onChange={onRateContent} 
            readonly={readonly}
            showLabel
          />
        </div>
      )}

      {/* Season Ratings */}
      {activeTab === 'seasons' && (
        <div className="space-y-3 max-h-60 overflow-y-auto">
          {seasons.map((season) => (
            <div key={season.season_number} className="flex items-center justify-between bg-zinc-800 p-3 rounded-lg">
              <span className="text-white font-medium">
                Season {season.season_number}
              </span>
              <AwardRating
                value={seasonRatings[season.season_number] || 0}
                onChange={(rating) => onRateSeason?.(season.season_number, rating)}
                readonly={readonly}
                size="sm"
                showValue
              />
            </div>
          ))}
          {seasons.length === 0 && (
            <p className="text-gray-500 text-center py-4">No season information available</p>
          )}
        </div>
      )}

      {/* Episode Ratings */}
      {activeTab === 'episodes' && (
        <div className="space-y-3">
          {/* Season Selector */}
          <select
            value={selectedSeason || ''}
            onChange={(e) => setSelectedSeason(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full bg-zinc-800 text-white border border-zinc-700 rounded-lg p-2"
          >
            <option value="">Select a season</option>
            {seasons.map((season) => (
              <option key={season.season_number} value={season.season_number}>
                Season {season.season_number}
              </option>
            ))}
          </select>

          {/* Episode List */}
          {selectedSeason && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(seasons.find(s => s.season_number === selectedSeason)?.episodes || []).map((episode) => (
                <div key={episode.episode_number} className="flex items-center justify-between bg-zinc-800 p-2 rounded-lg">
                  <div className="flex-1 min-w-0 mr-2">
                    <span className="text-white text-sm">
                      E{episode.episode_number}: {episode.name || `Episode ${episode.episode_number}`}
                    </span>
                  </div>
                  <AwardRating
                    value={episodeRatings[`${selectedSeason}_${episode.episode_number}`] || 0}
                    onChange={(rating) => onRateEpisode?.(selectedSeason, episode.episode_number, rating)}
                    readonly={readonly}
                    size="sm"
                    showValue={false}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Aggregate Rating Display
 */
export const AggregateRating = ({ 
  averageRating = 0, 
  totalRatings = 0,
  size = 'md'
}) => {
  return (
    <div className="flex items-center gap-3">
      <AwardRating value={averageRating} readonly size={size} showValue={false} />
      <div className="flex flex-col">
        <span className="text-yellow-500 font-bold text-lg">{averageRating.toFixed(1)}</span>
        <span className="text-gray-500 text-xs">{totalRatings} {totalRatings === 1 ? 'rating' : 'ratings'}</span>
      </div>
    </div>
  );
};

export default AwardRating;
